// src/pages/DevTicketDetail.js — one dev ticket as a conversation (2026-10-23).
// Same layout and parts as the ticket page: the request on top, the dev thread
// oldest → newest, emailed replies with their quoted chain folded away, and a
// details panel (status, priority, developers, notes, the client ticket it came from).
// Messages here go to the assigned developers and whoever raised it — never the client.
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Send, Ticket as TicketIcon, Search, ChevronDown, Code } from "lucide-react";
import { getAgents } from "../api";
import { fmtTkt } from "./DevTickets";
import { useAuth } from "../context/AuthContext";
import { MiniAvatar } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import { HtmlContent } from "../components/HtmlContent";
import {
  ConvoStyles, Timeline, Chips, KeyHints, JumpLatest, Avatar, Sel, PillSel, TopResizeHandle,
  STATUS_TONE, PRIORITY_TONE, previewText, stripHtml, fmtFull, isTypingTarget,
} from "../components/conversation/Conversation";

const API   = (process.env.REACT_APP_API_URL || "https://ticketing.midtc.com") + "/api";
const token = () => localStorage.getItem("midtc_token") || "";
const authed = (path, opts = {}) => fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}`, ...(opts.headers || {}) } });

// The quoted chain under an emailed dev reply — collapsed by default; the new words
// are the signal, the chain is reference.
function QuotedChain({ items }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <button type="button" className="cv-link" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        {open ? "Hide" : "Show"} quoted chain ({items.length} earlier {items.length === 1 ? "message" : "messages"})
      </button>
      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((q, i) => (
            <div key={i} className="cv-quoted">
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                <strong style={{ color: "var(--t-text-primary)" }}>{q.author || "Unknown sender"}</strong>
                {q.email && <span style={{ color: "var(--t-text-faint)" }}> &lt;{q.email}&gt;</span>}
                {q.date && <span style={{ color: "var(--t-text-faint)" }}> · {q.date}</span>}
              </div>
              {q.subject && <div style={{ fontSize: 11.5, color: "var(--t-text-faint)", fontStyle: "italic", marginBottom: 4 }}>{q.subject}</div>}
              <div style={{ fontSize: 13, maxHeight: 300, overflowY: "auto" }}><HtmlContent text={q.body} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// Module-level (one function forever), so typing a message never redraws the conversation.
const renderQuotedChain = (items) => <QuotedChain items={items} />;

// One fresh page per dev ticket (see TicketDetail): nothing carries over between ids.
export default function DevTicketDetail() {
  const { id } = useParams();
  return <DevTicketDetailPage key={id} />;
}

function DevTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { agent } = useAuth();
  const isMobile = useIsMobile();
  const embed = location.pathname.startsWith("/embed");
  const listPath = embed ? "/embed/dev-tickets" : "/dev-tickets";

  const [dt,       setDt]       = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents,   setAgents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [saveMsg,  setSaveMsg]  = useState("");
  const [filter,   setFilter]   = useState("all");
  const [search,   setSearch]   = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [focusId,  setFocusId]  = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [msg,      setMsg]      = useState(() => { try { return sessionStorage.getItem(`midtc_devdraft_${id}`) || ""; } catch { return ""; } });
  const [sending,  setSending]  = useState(false);
  const [sendErr,  setSendErr]  = useState("");
  const scrollRef = useRef(null);
  const nodeRefs  = useRef(new Map());
  const seenIds   = useRef(null);
  const textRef   = useRef(null);
  const nearBottom = useRef(true);

  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    const prev = main.style.overflow;
    main.style.overflow = "hidden";
    return () => { main.style.overflow = prev; };
  }, []);
  useEffect(() => { try { if (msg) sessionStorage.setItem(`midtc_devdraft_${id}`, msg); else sessionStorage.removeItem(`midtc_devdraft_${id}`); } catch { /* ignore */ } }, [msg, id]);

  const loadTicket = useCallback(async () => {
    // The dev list IS this page's data source (there is no GET /api/dev-tickets/:id). A failed or
    // rate-limited (429) list fetch must never read as "this ticket doesn't exist": keep whatever is
    // already on screen and flag a transient error instead. Only a good response is authoritative
    // for notFound — that is the one case where the ticket is genuinely gone.
    let res;
    try { res = await authed("/dev-tickets"); }
    catch { setLoadError(true); return null; }
    if (!res.ok) { setLoadError(true); return null; }
    const list = await res.json().catch(() => null);
    if (!Array.isArray(list)) { setLoadError(true); return null; }
    setLoadError(false);
    const found = list.find(d => d.id === id) || null;
    setDt(found); setNotFound(!found);
    return found;
  }, [id]);
  const loadMessages = useCallback(async () => {
    const res = await authed(`/dev-tickets/${id}/messages`);
    const data = await res.json().catch(() => []);
    if (res.ok) setMessages(Array.isArray(data) ? data : []);
  }, [id]);

  useEffect(() => {
    let alive = true;
    setLoading(true); seenIds.current = null;
    Promise.all([loadTicket(), loadMessages(), getAgents().then(a => { if (alive) setAgents(a.data || []); }).catch(() => {})])
      .catch(console.error).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [loadTicket, loadMessages]);
  // Messages poll every 15 s (email replies land here); the ticket itself every 60 s.
  useEffect(() => { const t = setInterval(() => { if (!sending) loadMessages().catch(() => {}); }, 15000); return () => clearInterval(t); }, [loadMessages, sending]);
  useEffect(() => { const t = setInterval(() => { if (!updating) loadTicket().catch(() => {}); }, 60000); return () => clearInterval(t); }, [loadTicket, updating]);

  const devIds = useMemo(() => new Set(agents.filter(a => [a.role, ...(a.roles || [])].includes("dev")).map(a => a.id)), [agents]);
  const entries = useMemo(() => {
    if (!dt) return [];
    const out = [{
      id: "req", kind: "agent", tagKind: "tech", role: "Dev request", name: dt.created_by_name || "MIDTC staff",
      to: `To ${(dt.assigned_dev_details || []).map(d => d.name).join(", ") || "the dev team"}`,
      at: dt.created_at, body: dt.description || dt.source_description || "", files: [], badges: dt.ticket_number ? [`from ${fmtTkt(dt.ticket_number)}`] : [],
    }];
    for (const m of messages) {
      // Notes the system writes ("Closed automatically because TKT-… was closed.") read as one line.
      if (m.source === "system") { out.push({ id: `m-${m.id}`, kind: "event", text: m.body, at: m.created_at, name: "", body: m.body }); continue; }
      const isDev = m.agent_id && devIds.has(m.agent_id);
      const name = m.agent_name_resolved || m.agent_name || "Unknown";
      out.push({
        id: `m-${m.id}`, kind: isDev ? "agent" : "client", tagKind: isDev ? "tech" : "client",
        role: isDev ? "Developer" : (m.agent_id ? "Technician" : "Staff"), name,
        to: m.source === "email" ? "Replied by email" : "", at: m.created_at, body: m.body,
        parsed: m.parsed || null, files: [], badges: m.agent_id && m.agent_id === agent?.id ? ["You"] : [],
        mine: m.agent_id && m.agent_id === agent?.id, isDev,
      });
    }
    for (const e of out) { e.preview = previewText(e.parsed ? (e.parsed.reply || "") : e.body); e.search = `${e.name} ${stripHtml(e.parsed ? e.parsed.reply : e.body)}`.toLowerCase(); }
    return out;
  }, [dt, messages, devIds, agent]);

  const FILTERS = { all: () => true, devs: e => e.isDev, others: e => e.id !== "req" && !e.isDev && e.kind !== "event", mine: e => e.mine };
  const counts = { all: entries.length, devs: entries.filter(FILTERS.devs).length, others: entries.filter(FILTERS.others).length, mine: entries.filter(FILTERS.mine).length };
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e => (FILTERS[filter] || FILTERS.all)(e) && (!q || e.search.includes(q)));
  }, [entries, filter, search]); // eslint-disable-line react-hooks/exhaustive-deps
  const messageIds = visible.filter(e => e.kind !== "event").map(e => e.id);

  useEffect(() => {
    if (!entries.length) return;
    if (!seenIds.current) {
      const open = new Set(["req"]);
      entries.slice(-2).forEach(e => open.add(e.id));
      setExpanded(open);
      seenIds.current = new Set(entries.map(e => e.id));
      return;
    }
    const fresh = entries.filter(e => !seenIds.current.has(e.id));
    entries.forEach(e => seenIds.current.add(e.id));
    if (fresh.length) setExpanded(prev => { const n = new Set(prev); fresh.forEach(e => n.add(e.id)); return n; });
  }, [entries]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => { nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160; };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && entries.length && nearBottom.current) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [entries.length, loading]);

  const toggle = useCallback((eid) => setExpanded(prev => { const n = new Set(prev); n.has(eid) ? n.delete(eid) : n.add(eid); return n; }), []);
  const refFor = useCallback((eid) => (node) => { if (node) nodeRefs.current.set(eid, node); else nodeRefs.current.delete(eid); }, []);
  const allOpen = messageIds.length > 0 && messageIds.every(x => expanded.has(x));
  const openComposer = useCallback(() => { setComposeOpen(true); setTimeout(() => textRef.current?.focus(), 30); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      const k = e.key.toLowerCase();
      if (k === "j" || k === "k") {
        if (!messageIds.length) return;
        e.preventDefault();
        const i = messageIds.indexOf(focusId);
        const next = k === "j" ? Math.min(messageIds.length - 1, i < 0 ? 0 : i + 1) : Math.max(0, i < 0 ? messageIds.length - 1 : i - 1);
        setFocusId(messageIds[next]);
        nodeRefs.current.get(messageIds[next])?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } else if (k === "enter" && focusId) { e.preventDefault(); toggle(focusId); }
      else if (k === "r") { e.preventDefault(); openComposer(); }
      else if (k === "e") { e.preventDefault(); allOpen ? setExpanded(new Set()) : setExpanded(new Set(entries.map(x => x.id))); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const update = async (patch) => {
    setUpdating(true); setSaveMsg("");
    try {
      const res = await authed(`/dev-tickets/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not save (${res.status})`);
      setDt(prev => ({ ...prev, ...data }));
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(""), 1500);
    } catch (err) { setSaveMsg(err.message); }
    finally { setUpdating(false); }
  };

  const send = async () => {
    if (!msg.trim() || sending) return;
    setSending(true); setSendErr("");
    try {
      const res = await authed(`/dev-tickets/${id}/messages`, { method: "POST", body: JSON.stringify({ body: msg.trim() }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Send failed (${res.status})`); }
      setMsg(""); setComposeOpen(false); nearBottom.current = true;
      await loadMessages();
    } catch (err) { setSendErr(`${err.message} — your message is still here.`); }
    finally { setSending(false); }
  };

  const openSource = () => {
    if (!dt?.source_ticket_id) return;
    if (embed) window.open(`https://ticketing.midtc.com/tickets/${dt.source_ticket_id}`, "_blank", "noopener");
    else navigate(`/tickets/${dt.source_ticket_id}`);
  };

  const spinner = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <div className="animate-spin" style={{ width: 28, height: 28, border: "3px solid var(--t-accent)", borderTopColor: "transparent", borderRadius: "50%" }} />
    </div>
  );
  if (loading && !dt) return spinner;
  // "Not found" is shown only when the server positively confirmed the ticket is gone — never because
  // the list fetch failed or was rate-limited (that keeps the current view / shows a retry below).
  if (notFound) return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--t-text-faint)" }}>
      This dev ticket wasn't found. <button className="cv-link" onClick={() => navigate(listPath)}>Back to the Dev Queue</button>
    </div>
  );
  if (!dt) {
    if (loadError) return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--t-text-faint)" }}>
        Couldn't reach the server just now — the ticket is still there.{" "}
        <button className="cv-link" onClick={() => { setLoadError(false); setLoading(true); Promise.all([loadTicket(), loadMessages()]).finally(() => setLoading(false)); }}>Try again</button>
      </div>
    );
    return spinner;
  }

  const devs = dt.assigned_dev_details || [];
  const chips = [["all", "All"], ["devs", "Developers"], ["others", "Technicians"], ["mine", "Mine"]].map(([key, label]) => ({ key, label, count: counts[key] }));

  return (
    <div className="cv-root" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: embed ? "100%" : undefined, overflow: isMobile ? "auto" : "hidden" }}>
      <ConvoStyles />
      <header style={{ padding: isMobile ? "14px 16px" : "16px 30px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", flexDirection: "column", gap: 12, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="cv-link" onClick={() => navigate(listPath)} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--t-text-muted)", fontWeight: 500 }}>
            <ArrowLeft size={15} /> Dev Queue
          </button>
          {dt.ticket_number && <><span style={{ color: "var(--t-text-faint)" }}>/</span><span style={{ fontWeight: 700, color: "var(--t-accent)", fontSize: 13 }}>{fmtTkt(dt.ticket_number)}</span></>}
          {updating && <span className="animate-spin" style={{ width: 13, height: 13, border: "2px solid var(--t-accent)", borderTopColor: "transparent", borderRadius: "50%" }} />}
          {saveMsg && <span role="status" style={{ fontSize: 12, color: saveMsg === "Saved" ? "var(--badge-green)" : "var(--badge-red)" }}>{saveMsg}</span>}
          <div style={{ flex: 1 }} />
          {dt.source_ticket_id && <button type="button" className="cv-btn" onClick={openSource}><TicketIcon size={16} /> Open the client ticket</button>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: isMobile ? 19 : 22, lineHeight: 1.3, overflowWrap: "anywhere" }}>{dt.title}</h1>
          <PillSel label="Status" value={dt.status} tone={STATUS_TONE[dt.status] || "gray"} options={["Open", "In Progress", "On Hold", "Resolved", "Closed"]} onChange={e => update({ status: e.target.value })} disabled={updating} />
          <PillSel label="Priority" value={dt.priority} tone={PRIORITY_TONE[dt.priority] || "gray"} options={["Critical", "High", "Medium", "Low"]} onChange={e => update({ priority: e.target.value })} disabled={updating} />
          <span className="cv-pill cv-c-gray">Staff only</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 13 }}>
          {dt.client_name && <span style={{ color: "var(--t-text-muted)" }}>{dt.client_name}</span>}
          <span style={{ color: "var(--t-text-faint)" }}>Sent by {dt.created_by_name || "staff"} · {fmtFull(dt.created_at)}</span>
          <div style={{ flex: 1 }} />
          <span style={{ color: "var(--t-text-faint)" }}>{devs.length ? "Developers" : "No developer assigned"}</span>
          {devs.map(d => <span key={d.id} title={d.name}><MiniAvatar name={d.name} size={26} /></span>)}
        </div>
      </header>

      <div style={{ flex: isMobile ? "none" : 1, minHeight: 0, display: "flex", flexDirection: isMobile ? "column" : "row" }}>
        <section aria-label="Dev conversation" style={{ flex: isMobile ? "none" : 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: isMobile ? "10px 16px" : "12px 30px", borderBottom: "1px solid var(--t-border-softer)", flexShrink: 0 }}>
            <Chips items={chips} value={filter} onChange={setFilter} />
            <div style={{ flex: 1 }} />
            <label className="cv-input" style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 12px", width: isMobile ? "100%" : 230 }}>
              <Search size={15} aria-hidden="true" style={{ color: "var(--t-text-faint)" }} />
              <input type="search" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search this conversation" placeholder="Search this conversation"
                style={{ border: 0, background: "transparent", color: "inherit", fontFamily: "inherit", fontSize: 13, outline: "none", width: "100%" }} />
            </label>
            <button type="button" className="cv-btn cv-btn-ghost" onClick={() => allOpen ? setExpanded(new Set()) : setExpanded(new Set(entries.map(x => x.id)))}>{allOpen ? "Collapse all" : "Expand all"}</button>
          </div>
          <div style={{ position: "relative", flex: isMobile ? "none" : 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div ref={scrollRef} style={{ flex: isMobile ? "none" : 1, minHeight: isMobile ? "auto" : 0, overflowY: isMobile ? "visible" : "auto", padding: isMobile ? "16px 12px 24px" : "22px 30px 30px" }}>
              <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
                {visible.length === 0 && <div style={{ textAlign: "center", color: "var(--t-text-faint)", fontSize: 13, padding: 32 }}>{search ? `Nothing matches “${search}”.` : "Nothing to show for this filter."}</div>}
                <Timeline entries={visible} expanded={expanded} toggle={toggle} focusedId={focusId} refFor={refFor} onReply={openComposer}
                  renderQuoted={renderQuotedChain} />
                {messages.length === 0 && (
                  <div style={{ textAlign: "center", color: "var(--t-text-faint)", fontSize: 13, lineHeight: 1.6, padding: "10px 0" }}>
                    No messages yet. Anything sent here emails the assigned developers and whoever raised it — their email replies land back in this thread.
                  </div>
                )}
                <KeyHints items={[[["J", "K"], "next / previous"], [["Enter"], "open"], [["R"], "write"], [["E"], "expand all"]]} />
              </div>
            </div>
            <JumpLatest scrollRef={scrollRef} bump={entries.length} />
          </div>

          {!composeOpen ? (
            <div className="cv-compose" style={{ padding: isMobile ? "12px 14px" : "14px 30px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <Avatar name={`${agent?.firstName || agent?.first_name || ""} ${agent?.lastName || agent?.last_name || ""}`} kind="tech" />
              <button type="button" className="cv-input" onClick={openComposer} style={{ flex: 1, height: 44, textAlign: "left", padding: "0 16px", color: "var(--t-text-faint)", fontSize: 14, cursor: "text" }}>
                {msg ? `Draft: ${previewText(msg, 70)}` : "Write to the dev team…"}
              </button>
            </div>
          ) : (
            <div className="cv-compose" style={{ padding: isMobile ? 14 : "16px 30px 18px", display: "flex", flexDirection: "column", gap: 12, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>To {devs.map(d => d.name).join(", ") || "the dev team"}{dt.created_by_name ? ` and ${dt.created_by_name}` : ""} · never the client</span>
                <div style={{ flex: 1 }} />
                <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Minimise (your text is kept)" title="Minimise" onClick={() => setComposeOpen(false)}><ChevronDown size={16} /></button>
              </div>
              <div className="cv-grow">
              <TopResizeHandle targetRef={textRef} min={100} />
              <textarea ref={textRef} className="cv-input" rows={4} value={msg} onChange={e => setMsg(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } if (e.key === "Escape") setComposeOpen(false); }}
                aria-label="Message the dev team" placeholder="Write to the devs. Enter sends, Shift+Enter starts a new line."
                style={{ resize: "vertical", padding: "13px 15px", fontSize: 14, lineHeight: 1.6, minHeight: 100 }} />
              </div>
              {sendErr && <div role="alert" style={{ fontSize: 12.5, color: "var(--badge-red)" }}>{sendErr}</div>}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Insert a code block" title="Code block" onClick={() => { setMsg(m => `${m}${m && !m.endsWith("\n") ? "\n" : ""}\`\`\`\n\n\`\`\``); textRef.current?.focus(); }}><Code size={16} /></button>
                <div style={{ flex: 1 }} />
                <button type="button" className="cv-btn cv-btn-primary" onClick={send} disabled={sending || !msg.trim()}><Send size={15} /> {sending ? "Sending…" : "Send"}</button>
              </div>
            </div>
          )}
        </section>

        <aside aria-label="Dev ticket details" style={{ width: isMobile ? "100%" : 340, flexShrink: 0, minHeight: 0, overflowY: isMobile ? "visible" : "auto", borderLeft: isMobile ? "none" : "1px solid var(--t-border-soft)", borderTop: isMobile ? "1px solid var(--t-border-soft)" : "none", background: "var(--t-gloss, none), var(--t-panel)" }}>
          <div className="cv-sect">
            <span className="cv-lbl">Dev ticket</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "var(--t-text-faint)" }}>Status</span>
                <Sel label="Status" value={dt.status} disabled={updating} onChange={e => update({ status: e.target.value })}>
                  {["Open", "In Progress", "On Hold", "Resolved", "Closed"].map(o => <option key={o} value={o}>{o}</option>)}
                </Sel>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "var(--t-text-faint)" }}>Priority</span>
                <Sel label="Priority" value={dt.priority} disabled={updating} onChange={e => update({ priority: e.target.value })}>
                  {["Critical", "High", "Medium", "Low"].map(o => <option key={o} value={o}>{o}</option>)}
                </Sel>
              </label>
            </div>
            <div className="cv-kv"><span>Type</span><span style={{ color: "var(--t-text-muted)" }}>{dt.type || "—"}</span></div>
            <div className="cv-kv"><span>Created</span><span style={{ color: "var(--t-text-muted)", textAlign: "right" }}>{fmtFull(dt.created_at)}</span></div>
            <div className="cv-kv"><span>Last update</span><span style={{ color: "var(--t-text-muted)", textAlign: "right" }}>{fmtFull(dt.updated_at)}</span></div>
          </div>
          <div className="cv-sect">
            <span className="cv-lbl">Developers</span>
            {devs.length === 0 && <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>No developer assigned.</span>}
            {devs.map(d => (
              <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <MiniAvatar name={d.name} size={30} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t-text-primary)" }}>{d.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--t-text-faint)", overflow: "hidden", textOverflow: "ellipsis" }}>{d.email}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="cv-sect">
            <span className="cv-lbl">Client ticket</span>
            {dt.source_ticket_id ? (
              <>
                <button type="button" className="cv-link" style={{ textAlign: "left" }} onClick={openSource}>{fmtTkt(dt.ticket_number)} · {dt.source_subject || "Open the ticket"}</button>
                <div className="cv-kv"><span>Client</span><span style={{ color: "var(--t-text-muted)" }}>{dt.client_name || "—"}</span></div>
                <div className="cv-kv"><span>Ticket status</span><span style={{ color: "var(--t-text-muted)" }}>{dt.source_status || "—"}</span></div>
              </>
            ) : <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>Not linked to a client ticket.</span>}
          </div>
          <div className="cv-sect" style={{ borderBottom: 0 }}>
            <span className="cv-lbl">Notes</span>
            <textarea key={dt.id} className="cv-input" defaultValue={dt.notes || ""} rows={4} aria-label="Dev ticket notes"
              onBlur={e => { if ((e.target.value || "") !== (dt.notes || "")) update({ notes: e.target.value }); }}
              placeholder="Notes for the dev team — saved when you click away."
              style={{ resize: "vertical", padding: "10px 12px", fontSize: 13, lineHeight: 1.6, borderRadius: "var(--t-radius-sm,8px)" }} />
          </div>
        </aside>
      </div>
    </div>
  );
}
